import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { User, Mail, MapPin, Fish, Crown, Calendar, Edit2, Save, X, Cloud, Thermometer, Wind, Droplets, ShoppingBag, Receipt, Package, Bell, BellOff, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Link } from 'react-router-dom';
import BadgeCard from '../components/BadgeCard';
import CatchImage from '../components/CatchImage';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user, token, updateUser } = useAuth();
  const { permission, isSupported, requestPermission } = useNotifications();
  const [editing, setEditing] = useState(false);
  const [myCatches, setMyCatches] = useState([]);
  const [orders, setOrders] = useState([]);
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [badges, setBadges] = useState(null);
  const [form, setForm] = useState({
    username: user?.username || '',
    bio: user?.bio || '',
    location: user?.location || ''
  });

  useEffect(() => {
    fetchMyCatches();
    fetchWeather();
    fetchOrders();
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      const r = await axios.get(`${API}/badges/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBadges(r.data);
    } catch {
      setBadges({ earned: [], locked: [], earned_count: 0, total: 0 });
    }
  };

  const fetchMyCatches = async () => {
    try {
      const response = await axios.get(`${API}/catches?user_id=${user?.id}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyCatches(response.data);
    } catch (error) {
      console.error('Failed to fetch catches:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/user/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  const fetchWeather = async () => {
    setWeatherLoading(true);
    try {
      // Get user's location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          const { latitude, longitude } = position.coords;
          // Using Open-Meteo free API
          const response = await axios.get(
            `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max&timezone=auto`
          );
          setWeather(response.data);
          setWeatherLoading(false);
        }, () => {
          // Default to London if location denied
          fetchWeatherByCoords(51.5074, -0.1278);
        });
      } else {
        fetchWeatherByCoords(51.5074, -0.1278);
      }
    } catch (error) {
      console.error('Failed to fetch weather:', error);
      setWeatherLoading(false);
    }
  };

  const fetchWeatherByCoords = async (lat, lon) => {
    try {
      const response = await axios.get(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max&timezone=auto`
      );
      setWeather(response.data);
    } catch (error) {
      console.error('Failed to fetch weather:', error);
    } finally {
      setWeatherLoading(false);
    }
  };

  const getWeatherDescription = (code) => {
    const descriptions = {
      0: 'Clear sky',
      1: 'Mainly clear',
      2: 'Partly cloudy',
      3: 'Overcast',
      45: 'Foggy',
      48: 'Depositing rime fog',
      51: 'Light drizzle',
      53: 'Moderate drizzle',
      55: 'Dense drizzle',
      61: 'Slight rain',
      63: 'Moderate rain',
      65: 'Heavy rain',
      71: 'Slight snow',
      73: 'Moderate snow',
      75: 'Heavy snow',
      80: 'Slight rain showers',
      81: 'Moderate rain showers',
      82: 'Violent rain showers',
      95: 'Thunderstorm'
    };
    return descriptions[code] || 'Unknown';
  };

  const handleSave = async () => {
    try {
      const response = await axios.put(`${API}/auth/profile`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateUser(response.data);
      toast.success('Profile updated!');
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const getTierStyles = (tier) => {
    switch (tier) {
      case 'elite': return 'text-[#c8b273] border-[#c8b273]';
      case 'pro': return 'text-[#ff5e00] border-[#ff5e00]';
      default: return 'text-[#6b7d6b] border-[#6b7d6b]';
    }
  };

  return (
    <div className="min-h-screen p-6 md:p-8" data-testid="profile-page">
      <div className="max-w-4xl mx-auto">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="lg:col-span-2">
            <motion.div
              className="glass-card p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-[#141714] flex items-center justify-center">
                    <User className="w-10 h-10 text-[#c8b273]" />
                  </div>
                  <div>
                    {editing ? (
                      <Input
                        value={form.username}
                        onChange={(e) => setForm(p => ({ ...p, username: e.target.value }))}
                        className="bg-[#141714] border-white/10 text-white text-xl font-bold"
                      />
                    ) : (
                      <h1 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white">
                        {user?.username}
                      </h1>
                    )}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Crown className={`w-4 h-4 ${getTierStyles(user?.subscription_tier)}`} />
                      <span className={`text-sm font-bold uppercase ${getTierStyles(user?.subscription_tier)}`}>
                        {user?.subscription_tier} Member
                      </span>
                      {user?.billing_interval === 'year' && (
                        <span className="text-[10px] uppercase tracking-wider bg-[#c8b273] text-black px-2 py-0.5 rounded font-bold" data-testid="yearly-badge">
                          Yearly
                        </span>
                      )}
                      {user?.subscription_renews_at && (
                        <span className="text-[11px] text-[#6b7d6b]" data-testid="renews-at">
                          Renews {new Date(user.subscription_renews_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {editing ? (
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => setEditing(false)} className="text-[#8a948a]">
                      <X className="w-4 h-4" />
                    </Button>
                    <Button className="btn-gold" onClick={handleSave} data-testid="save-profile-btn">
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                ) : (
                  <Button variant="ghost" className="btn-ghost" onClick={() => setEditing(true)} data-testid="edit-profile-btn">
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="tactical-label">Email</label>
                  <div className="flex items-center gap-2 text-[#8a948a]">
                    <Mail className="w-4 h-4" />
                    {user?.email}
                  </div>
                </div>

                <div>
                  <label className="tactical-label">Location</label>
                  {editing ? (
                    <Input
                      value={form.location}
                      onChange={(e) => setForm(p => ({ ...p, location: e.target.value }))}
                      className="bg-[#141714] border-white/10 text-white"
                      placeholder="e.g., Essex, UK"
                    />
                  ) : (
                    <div className="flex items-center gap-2 text-[#8a948a]">
                      <MapPin className="w-4 h-4" />
                      {user?.location || 'Not set'}
                    </div>
                  )}
                </div>

                <div>
                  <label className="tactical-label">Bio</label>
                  {editing ? (
                    <Textarea
                      value={form.bio}
                      onChange={(e) => setForm(p => ({ ...p, bio: e.target.value }))}
                      className="bg-[#141714] border-white/10 text-white"
                      placeholder="Tell us about yourself..."
                      rows={3}
                    />
                  ) : (
                    <p className="text-[#8a948a]">{user?.bio || 'No bio yet'}</p>
                  )}
                </div>

                <div className="flex items-center gap-6 pt-4 border-t border-white/5">
                  <div>
                    <p className="font-['Barlow_Condensed'] text-2xl font-bold text-[#c8b273]">
                      {user?.catches_count || 0}
                    </p>
                    <p className="data-label text-[#8a948a] text-xs">Total Catches</p>
                  </div>
                  <div>
                    <p className="font-['Barlow_Condensed'] text-2xl font-bold text-white">
                      {new Date(user?.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                    </p>
                    <p className="data-label text-[#8a948a] text-xs">Member Since</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Achievements / Badges */}
            <motion.div
              className="glass-card p-6 mt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              data-testid="profile-badges-section"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#c8b273]" />
                  Achievements
                </h2>
                {badges && (
                  <span className="text-xs text-[#c8b273] font-bold uppercase tracking-widest">
                    {badges.earned_count} / {badges.total}
                  </span>
                )}
              </div>

              {!badges ? (
                <p className="text-sm text-[#8a948a]">Loading…</p>
              ) : badges.earned.length === 0 ? (
                <div className="text-center py-6">
                  <Award className="w-10 h-10 text-[#4a504a] mx-auto mb-3" />
                  <p className="text-[#8a948a] text-sm mb-3">Log a catch to start unlocking badges</p>
                  <Link
                    to="/competitions"
                    className="inline-flex items-center gap-2 text-xs text-[#c8b273] hover:text-[#d8c283] uppercase tracking-widest"
                  >
                    See all achievements →
                  </Link>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {badges.earned.slice(0, 6).map((b) => (
                      <BadgeCard key={b.code} badge={b} earned />
                    ))}
                  </div>
                  {badges.locked.length > 0 && (
                    <div className="mt-4 text-center">
                      <Link
                        to="/competitions"
                        className="inline-flex items-center gap-2 text-xs text-[#c8b273] hover:text-[#d8c283] uppercase tracking-widest"
                        data-testid="profile-see-all-badges"
                      >
                        {badges.locked.length} more to unlock →
                      </Link>
                    </div>
                  )}
                </>
              )}
            </motion.div>

            {/* My Catches */}
            <motion.div
              className="glass-card p-6 mt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-4">
                My Recent Catches
              </h2>
              {myCatches.length === 0 ? (
                <div className="text-center py-8">
                  <Fish className="w-12 h-12 text-[#4a504a] mx-auto mb-4" />
                  <p className="text-[#8a948a]">No catches logged yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {myCatches.map((catch_) => (
                    <div key={catch_.id} className="flex items-center gap-4 p-3 bg-[#141714] rounded">
                      <CatchImage row={catch_} aspectRatio="square" rounded className="w-12 h-12 flex-shrink-0" testId={`profile-catch-image-${catch_.id}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-bold truncate">{catch_.fish_type}</p>
                        <p className="text-[#8a948a] text-sm truncate">{catch_.lake_name}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-['Barlow_Condensed'] text-xl font-bold text-[#c8b273]">{catch_.weight}lb</p>
                        <p className="text-[#4a504a] text-xs">{new Date(catch_.caught_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Order History */}
            <motion.div
              className="glass-card p-6 mt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-4 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-[#c8b273]" />
                Order History
              </h2>
              {orders.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingBag className="w-12 h-12 text-[#4a504a] mx-auto mb-4" />
                  <p className="text-[#8a948a]">No orders yet</p>
                  <p className="text-[#6b7d6b] text-sm mt-1">Your purchases will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.slice(0, 5).map((order) => (
                    <div key={order.id} className="flex items-center gap-4 p-3 bg-[#141714] rounded">
                      <div className="w-10 h-10 rounded bg-[#0b0d0b] flex items-center justify-center">
                        {order.type === 'bait_subscription' ? (
                          <Package className="w-5 h-5 text-[#ff5e00]" />
                        ) : order.type === 'subscription' ? (
                          <Crown className="w-5 h-5 text-[#c8b273]" />
                        ) : (
                          <ShoppingBag className="w-5 h-5 text-[#6b7d6b]" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-bold text-sm">{order.description}</p>
                        <p className="text-[#8a948a] text-xs">
                          {new Date(order.created_at).toLocaleDateString('en-GB', { 
                            day: 'numeric', month: 'short', year: 'numeric' 
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-['Barlow_Condensed'] text-lg font-bold text-[#c8b273]">
                          £{order.amount?.toFixed(2)}
                        </p>
                        <p className="text-[#22c55e] text-xs uppercase">Paid</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Weather Widget */}
          <div>
            <motion.div
              className="glass-card p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-4 flex items-center gap-2">
                <Cloud className="w-5 h-5 text-[#c8b273]" />
                Live Weather
              </h2>
              
              {weatherLoading ? (
                <div className="space-y-4 animate-pulse">
                  <div className="h-20 bg-[#141714] rounded" />
                  <div className="h-16 bg-[#141714] rounded" />
                </div>
              ) : weather ? (
                <div>
                  <div className="text-center mb-6">
                    <p className="font-['Barlow_Condensed'] text-5xl font-bold text-white">
                      {Math.round(weather.current.temperature_2m)}°C
                    </p>
                    <p className="text-[#c8b273] mt-1">
                      {getWeatherDescription(weather.current.weather_code)}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="text-center p-3 bg-[#141714] rounded">
                      <Wind className="w-5 h-5 text-[#6b7d6b] mx-auto mb-1" />
                      <p className="text-white font-bold">{Math.round(weather.current.wind_speed_10m)}</p>
                      <p className="text-[#8a948a] text-xs">km/h</p>
                    </div>
                    <div className="text-center p-3 bg-[#141714] rounded">
                      <Droplets className="w-5 h-5 text-[#6b7d6b] mx-auto mb-1" />
                      <p className="text-white font-bold">{weather.current.relative_humidity_2m}%</p>
                      <p className="text-[#8a948a] text-xs">Humidity</p>
                    </div>
                    <div className="text-center p-3 bg-[#141714] rounded">
                      <Thermometer className="w-5 h-5 text-[#6b7d6b] mx-auto mb-1" />
                      <p className="text-white font-bold">{Math.round(weather.daily.temperature_2m_max[0])}°</p>
                      <p className="text-[#8a948a] text-xs">High</p>
                    </div>
                  </div>

                  <div className="border-t border-white/5 pt-4">
                    <p className="data-label text-[#8a948a] text-xs mb-3">3-Day Forecast</p>
                    <div className="space-y-2">
                      {weather.daily.time.slice(0, 3).map((date, i) => (
                        <div key={date} className="flex items-center justify-between text-sm">
                          <span className="text-[#8a948a]">
                            {i === 0 ? 'Today' : new Date(date).toLocaleDateString('en-GB', { weekday: 'short' })}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-[#3b82f6]">{Math.round(weather.daily.temperature_2m_min[i])}°</span>
                            <span className="text-white">/</span>
                            <span className="text-[#ff5e00]">{Math.round(weather.daily.temperature_2m_max[i])}°</span>
                          </div>
                          <span className="text-[#8a948a]">{weather.daily.precipitation_probability_max[i]}% rain</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-[#141714] rounded">
                    <p className="text-xs text-[#c8b273] font-bold uppercase tracking-wider mb-1">Fishing Tip</p>
                    <p className="text-[#8a948a] text-sm">
                      {weather.current.wind_speed_10m < 15 
                        ? 'Calm conditions - great for surface fishing!'
                        : weather.current.wind_speed_10m < 30
                        ? 'Moderate wind - try fishing the windward bank.'
                        : 'Strong winds - consider sheltered swims.'}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-[#8a948a] text-center py-4">Unable to load weather data</p>
              )}
            </motion.div>

            {/* Notification Settings */}
            <motion.div
              className="glass-card p-6 mt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#c8b273]" />
                Notifications
              </h2>
              
              {!isSupported ? (
                <p className="text-[#8a948a] text-sm">Push notifications are not supported in your browser.</p>
              ) : permission === 'granted' ? (
                <div className="flex items-center justify-between p-4 bg-[#141714] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                      <Bell className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Notifications Enabled</p>
                      <p className="text-[#8a948a] text-sm">You'll receive alerts for new messages, catches, and orders</p>
                    </div>
                  </div>
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                </div>
              ) : permission === 'denied' ? (
                <div className="flex items-center gap-3 p-4 bg-[#141714] rounded-lg">
                  <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                    <BellOff className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Notifications Blocked</p>
                    <p className="text-[#8a948a] text-sm">Enable notifications in your browser settings to receive alerts</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-[#8a948a] text-sm">
                    Get notified about new messages, catch reports from anglers you follow, and order updates.
                  </p>
                  <Button 
                    className="btn-gold w-full"
                    onClick={requestPermission}
                    data-testid="enable-notifications-btn"
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    Enable Push Notifications
                  </Button>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
